import type { ApiEngagementMessage } from "@bridge-applications/pb-types";
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import {
    playManagedSound,
    preloadSounds,
    type ManagedSoundPlayback,
} from "../helpers/audio";
import {
    INBOX_REWARD_SOUNDS,
    inboxRewardCollection,
    inboxRewardSound,
} from "../helpers/inboxRewardCollection";
import { REWARD_COLLECTION_TIMING } from "../helpers/rewardCollection";

const REWARD_SOUND_FADE_MS = 150;

interface UseInboxRewardCollectionOptions {
    isOpen: boolean;
    claim: (messageId: string) => Promise<ApiEngagementMessage>;
    scrollerRef: RefObject<HTMLDivElement | null>;
}

export function useInboxRewardCollection({
    isOpen,
    claim,
    scrollerRef,
}: UseInboxRewardCollectionOptions) {
    const { t } = useTranslation();
    const [queue, setQueue] = useState<ApiEngagementMessage[]>([]);
    const sessionRef = useRef<symbol | null>(null);
    const presentedIdsRef = useRef(new Set<string>());
    const playbackRef = useRef<ManagedSoundPlayback | null>(null);
    const scrollTopRef = useRef(0);
    const activeMessage = isOpen ? queue[0] : undefined;

    useLayoutEffect(() => {
        sessionRef.current = isOpen ? Symbol("inbox session") : null;
        if (!isOpen) setQueue([]);
        return () => {
            sessionRef.current = null;
        };
    }, [isOpen]);

    useLayoutEffect(() => {
        if (activeMessage)
            scrollTopRef.current = scrollerRef.current?.scrollTop ?? 0;
    }, [activeMessage, scrollerRef]);

    const fadeSound = useCallback(() => {
        playbackRef.current?.cancel(REWARD_SOUND_FADE_MS);
    }, []);

    useEffect(() => {
        if (!activeMessage) return;
        const playback = playManagedSound(inboxRewardSound(activeMessage), {
            delayMs: REWARD_COLLECTION_TIMING.revealDelayMs,
            fadeOutMs: REWARD_SOUND_FADE_MS,
            // Do not queue a stale reward cue in a suspended audio context.
            getOffsetSeconds: () => 0,
        });
        playbackRef.current = playback;
        const cancel = () => playback.cancel(0);
        const onVisibilityChange = () => {
            if (document.hidden) cancel();
        };
        window.addEventListener("blur", cancel);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            cancel();
            if (playbackRef.current === playback) playbackRef.current = null;
            window.removeEventListener("blur", cancel);
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
        };
    }, [activeMessage]);

    const claimAndPresent = useCallback(
        async (messageId: string) => {
            const session = sessionRef.current;
            preloadSounds(INBOX_REWARD_SOUNDS);
            const confirmed = await claim(messageId);
            if (
                session !== null &&
                sessionRef.current === session &&
                confirmed.id === messageId &&
                confirmed.rewardState === "CLAIMED" &&
                confirmed.rewards.length > 0 &&
                !presentedIdsRef.current.has(messageId)
            ) {
                presentedIdsRef.current.add(messageId);
                setQueue((current) => [...current, confirmed]);
            }
            return confirmed;
        },
        [claim],
    );

    const complete = useCallback(
        (messageId: string) => {
            if (activeMessage?.id !== messageId) return;
            fadeSound();
            setQueue((current) =>
                current[0]?.id === messageId ? current.slice(1) : current,
            );
            // The claimed button is now disabled; return focus without jumping to the title.
            const scroller = scrollerRef.current;
            scroller?.focus({ preventScroll: true });
            scroller?.scrollTo({
                top: scrollTopRef.current,
                behavior: "instant",
            });
        },
        [activeMessage, fadeSound, scrollerRef],
    );

    return {
        claimAndPresent,
        presentation: activeMessage
            ? inboxRewardCollection(activeMessage, t)
            : null,
        fadeSound,
        complete,
    };
}
